import { create } from 'zustand'
import { SETTINGS_KEYS } from '../config/environment'
import { featureFlagsService } from '../services/feature-flags.service'
import { settingsService } from '../services/settings.service'
import { DEFAULT_PACKAGE_FEATURE_FLAGS, Document, PackageFeatureFlags, SavedView, UiSettings, User } from '../types'

const STORE_LOGGING_ENABLED = process.env.NODE_ENV !== 'production'

function logStore(message: string, payload?: unknown) {
  if (!STORE_LOGGING_ENABLED) {
    return
  }

  if (payload == undefined) {
    // eslint-disable-next-line no-console
    console.debug(`[AppStore] ${message}`)
  }
}

interface AppState {
  // Auth & User
  user: User | null
  uiSettings: UiSettings | null
  isAuthenticated: boolean | null // null = loading, true = authenticated, false = not authenticated

  // UI State
  slimSidebar: boolean
  darkMode: boolean
  isMenuCollapsed: boolean

  // Data
  savedViews: SavedView[]
  openDocuments: Document[]

  // Settings
  settings: Record<string, any>
  featureFlags: PackageFeatureFlags

  // Actions
  setUser: (user: User | null) => void
  setUiSettings: (settings: UiSettings) => void
  setAuthenticated: (isAuth: boolean | null) => void
  setSlimSidebar: (slim: boolean) => void
  setDarkMode: (dark: boolean) => void
  setIsMenuCollapsed: (collapsed: boolean) => void
  setSavedViews: (views: SavedView[]) => void
  addOpenDocument: (doc: Document) => void
  removeOpenDocument: (docId: number) => void
  clearOpenDocuments: () => void
  getSetting: (key: string, defaultValue?: any) => any
  setSetting: (key: string, value: any) => void
  logout: () => void
  setFeatureFlags: (flags: PackageFeatureFlags) => void
  refreshFeatureFlags: () => Promise<void>
}

export const useAppStore = create<AppState>()((set, get) => {
  // Subscribe to settings service changes
  settingsService.subscribe(() => {
    const slimSidebarValue = settingsService.get(SETTINGS_KEYS.SLIM_SIDEBAR)
    if (slimSidebarValue !== undefined && slimSidebarValue !== get().slimSidebar) {
      set({ slimSidebar: Boolean(slimSidebarValue) })
      logStore('Slim sidebar updated via settings service', { slimSidebar: slimSidebarValue })
    }
  })

  return {
    // Initial state
    user: null,
    uiSettings: null,
    isAuthenticated: null, // loading state
    slimSidebar: false,
    darkMode: false,
    isMenuCollapsed: true,
    savedViews: [],
    openDocuments: [],
    settings: {},
    featureFlags: { ...DEFAULT_PACKAGE_FEATURE_FLAGS },

    // Actions
    setUser: (user) => {
      set({ user })
    },

    setUiSettings: (uiSettings) => {
      const settings = uiSettings.settings || {}

      // Pokušaj da čitaš postavku pod različitim ključevima
      const slimSidebarValue = settings[SETTINGS_KEYS.SLIM_SIDEBAR] || settings['general-settings:slim-sidebar'] || settings['slim-sidebar'] || false

      const nextState = {
        uiSettings,
        user: uiSettings.user,
        settings,
        slimSidebar: Boolean(slimSidebarValue),
        darkMode: settings[SETTINGS_KEYS.DARK_MODE_ENABLED] || false,
      }

      set(nextState)
    },

    setAuthenticated: (isAuth) => {
      const wasAuthenticated = get().isAuthenticated === true

      set({ isAuthenticated: isAuth })

      if (isAuth === true) {
        featureFlagsService.startPolling()
        void featureFlagsService
          .fetchFlags()
          .then((flags) => {
            set({ featureFlags: flags })
          })
          .catch(() => undefined)
        return
      }

      if (wasAuthenticated || isAuth === false) {
        featureFlagsService.stopPolling()
        set({ featureFlags: { ...DEFAULT_PACKAGE_FEATURE_FLAGS } })
      }
    },

    setSlimSidebar: (slim) => {
      set({ slimSidebar: slim })
      settingsService.set(SETTINGS_KEYS.SLIM_SIDEBAR, slim)
    },

    setDarkMode: (dark) => {
      set({ darkMode: dark })
      get().setSetting(SETTINGS_KEYS.DARK_MODE_ENABLED, dark)

      // Apply dark mode to document
      const html = document.documentElement
      if (dark) {
        html.setAttribute('data-bs-theme', 'dark')
      } else {
        html.setAttribute('data-bs-theme', 'light')
      }
    },

    setIsMenuCollapsed: (collapsed) => {
      set({ isMenuCollapsed: collapsed })
    },

    setSavedViews: (views) => {
      set({ savedViews: views })
    },

    addOpenDocument: (doc) => {
      const openDocs = get().openDocuments
      if (!openDocs.find((d) => d.id === doc.id)) {
        set({ openDocuments: [...openDocs, doc] })
      }
    },

    removeOpenDocument: (docId) => {
      set({ openDocuments: get().openDocuments.filter((d) => d.id !== docId) })
    },

    clearOpenDocuments: () => {
      set({ openDocuments: [] })
    },

    getSetting: (key, defaultValue = null) => {
      const settings = get().settings
      return settings[key] !== undefined ? settings[key] : defaultValue
    },

    setSetting: (key, value) => {
      set({ settings: { ...get().settings, [key]: value } })
    },

    logout: () => {
      featureFlagsService.stopPolling()
      const persistedTheme = settingsService.getPersistedThemePrefs()
      if (persistedTheme) {
        settingsService.updateAppearanceSettings(persistedTheme.useSystem, persistedTheme.enabled, persistedTheme.themeColor || undefined, false)
      } else {
        const html = document.documentElement
        html.setAttribute('data-bs-theme', 'light')
        document.body.classList.add('primary-light')
        document.body.classList.remove('primary-dark')
      }
      set({
        user: null,
        uiSettings: null,
        isAuthenticated: false,
        openDocuments: [],
        featureFlags: { ...DEFAULT_PACKAGE_FEATURE_FLAGS },
      })
    },

    setFeatureFlags: (flags) => {
      set({ featureFlags: flags })
    },

    refreshFeatureFlags: async () => {
      if (get().isAuthenticated !== true) {
        set({ featureFlags: { ...DEFAULT_PACKAGE_FEATURE_FLAGS } })
        return
      }

      try {
        const updated = await featureFlagsService.fetchFlags()
        set({ featureFlags: updated })
      } catch (error) {
        console.error('Failed to refresh feature flags', error)
      }
    },
  }
})
